import { TERMS_VERSION } from '@wyn/contracts';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import { UI } from '../icons/ui-icons.ts';
import { useAcceptTerms } from '../lib/queries.ts';
import { rt } from '../lib/runtime.ts';
import { Masthead } from './Layout.tsx';
import { ErrorNotice } from './Notice.tsx';
import { PageTitle } from './ui.tsx';

/** "5 October 2026", from the version, which is the date the rules or terms last changed. */
export const termsDate = new Date(`${TERMS_VERSION}T00:00:00Z`).toLocaleDateString('en-GB', { timeZone: 'UTC', day: 'numeric', month: 'long', year: 'numeric' });

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

/**
 * Month and year of birth, as `YYYY-MM` (empty until both are chosen). A neutral question: it doesn't
 * say what age is needed, so it doesn't suggest an answer. The server checks 13 and over.
 */
export function BirthMonth({ value, onChange }: { value: string; onChange: (born: string) => void }) {
  const [year, setYear] = useState(value.slice(0, 4));
  const [month, setMonth] = useState(value.slice(5, 7));
  const thisYear = new Date().getUTCFullYear();
  const pick = (y: string, m: string) => {
    setYear(y);
    setMonth(m);
    onChange(y && m ? `${y}-${m}` : '');
  };
  return (
    <fieldset className="space-y-1">
      <legend className="text-sm">When were you born?</legend>
      <div className="flex flex-wrap gap-2">
        <select aria-label="Birth month" className="field" value={month} onChange={(e) => pick(year, e.target.value)}>
          <option value="">Month</option>
          {MONTHS.map((m, i) => <option key={m} value={String(i + 1).padStart(2, '0')}>{m}</option>)}
        </select>
        <select aria-label="Birth year" className="field" value={year} onChange={(e) => pick(e.target.value, month)}>
          <option value="">Year</option>
          {Array.from({ length: 100 }, (_, i) => String(thisYear - i)).map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
      </div>
      <p className="text-sm text-ink-faint">We keep the month and year only, to set the right privacy defaults for your age.</p>
    </fieldset>
  );
}

/** The tick box every new character needs. The pages open in a new tab so the half-made character survives. */
export function AgreeToTerms({ agreed, onChange }: { agreed: boolean; onChange: (agreed: boolean) => void }) {
  const page = (to: '/rules' | '/terms' | '/privacy', text: string) => <Link to={to} target="_blank" className="text-gilt underline">{text}</Link>;
  return (
    <div className="space-y-1">
      <label className="flex items-start gap-2">
        <input type="checkbox" className="mt-1.5 accent-gilt" checked={agreed} onChange={(e) => onChange(e.target.checked)} />
        <span>I agree to the {page('/rules', 'rules')} and {page('/terms', 'terms')}.</span>
      </label>
      <p className="pl-6 text-sm text-ink-faint">The {page('/privacy', 'privacy notice')} says what we keep about you, and for how long.</p>
    </div>
  );
}

/**
 * In place of the game until the player agrees to the current rules and terms: after they change, or if
 * never asked. A player from before birth months were asked gives theirs here, once.
 */
export function TermsChanged({ firstTime, askBorn }: { firstTime: boolean; askBorn: boolean }) {
  const [agreed, setAgreed] = useState(false);
  const [born, setBorn] = useState('');
  const accept = useAcceptTerms();
  return (
    <div className="mx-auto flex min-h-dvh max-w-2xl flex-col px-4">
      <header className="border-b border-rule py-3"><Masthead /></header>
      <main className="panel my-10 space-y-4">
        <PageTitle icon={UI.rules}>{firstTime ? 'The rules of the realm' : 'The rules have changed'}</PageTitle>
        <p>
          {firstTime
            ? 'Wynchester now has written rules and terms, and a privacy notice. Please read them before you carry on.'
            : `The rules, terms or privacy notice changed on ${termsDate}. Please read them again before you carry on.`}
        </p>
        {askBorn && <BirthMonth value={born} onChange={setBorn} />}
        <AgreeToTerms agreed={agreed} onChange={setAgreed} />
        <div className="flex flex-wrap gap-3">
          <button type="button" className="btn" disabled={!agreed || (askBorn && !born) || accept.isPending} onClick={() => accept.mutate(askBorn ? born : undefined)}>Carry on →</button>
          <button type="button" className="btn-quiet" onClick={() => rt().auth.signOut()}>Sign out</button>
        </div>
        <ErrorNotice error={accept.error} />
      </main>
    </div>
  );
}
